/**
 * InspectionReminderConfigFields - Bloco de configuração exclusivo dos lembretes de vistoria
 *
 * Exibido dentro do ReminderRuleFormDialog quando entityType === 'vistoria'.
 * Configura: status que disparam, referência do deslocamento, tipos de responsável,
 * repetição e filtro opcional por Tipo de Checklist (reutiliza ChecklistTypeFilter).
 *
 * @version 1.1.0
 * RELEASE: 2026-10-03 v1.1.0 - Status finalizados (Aprovada, Reprovada, Cancelada) agrupados; forçam referência "entrada no status"
 * RELEASE: 2026-10-02 v1.0.0 - Criação (lembretes de vistorias na Central de Lembretes)
 */

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChecklistTypeFilter } from '@/components/checklist/filters/ChecklistTypeFilter';
import type { InspectionReminderConfig } from '@/hooks/useReminderRules';

/** Status que podem disparar lembrete (allowlist igual à do banco), agrupados em aberto/finalizada */
export const INSPECTION_REMINDER_STATUSES = [
  { value: 'pending', label: 'Pendente', group: 'open' },
  { value: 'in_progress', label: 'Em andamento', group: 'open' },
  { value: 'awaiting_review', label: 'Aguardando revisão', group: 'open' },
  { value: 'correction_requested', label: 'Correção solicitada', group: 'open' },
  { value: 'approved', label: 'Aprovada', group: 'final' },
  { value: 'rejected', label: 'Reprovada', group: 'final' },
  { value: 'cancelled', label: 'Cancelada', group: 'final' },
] as const;

/** Status finalizados: só aceitam a referência "entrada no status" (v1.1.0) */
export const INSPECTION_FINAL_STATUSES: string[] = ['approved', 'rejected', 'cancelled'];

/** Indica se a configuração possui algum status finalizado */
export function hasFinalInspectionStatus(statuses: string[]): boolean {
  return statuses.some((s) => INSPECTION_FINAL_STATUSES.includes(s));
}

/** Tipos de responsável que podem receber */
export const INSPECTION_RECIPIENT_MODES = [
  { value: 'user', label: 'Usuário interno' },
  { value: 'queue', label: 'Fila (usuário atribuído)' },
  { value: 'owner', label: 'Cliente/proprietário' },
] as const;

/** Referências do deslocamento */
export const INSPECTION_ANCHORS = [
  { value: 'status_entered', label: 'Entrada no status (tempo no status)' },
  { value: 'due_start', label: 'Prazo de início' },
  { value: 'due_finish', label: 'Prazo de conclusão' },
] as const;

/** Configuração padrão para nova regra */
export const DEFAULT_INSPECTION_CONFIG: InspectionReminderConfig = {
  statuses: ['pending'],
  anchor: 'status_entered',
  recipient_modes: ['user', 'queue', 'owner'],
  repeat: { mode: 'once' },
};

interface Props {
  value: InspectionReminderConfig;
  onChange: (next: InspectionReminderConfig) => void;
  /** Tipo de Checklist selecionado ("all" = todos) */
  checklistTypeId: string;
  onChecklistTypeChange: (value: string) => void;
}

/** Alterna um item numa lista (seleção múltipla por checkbox) */
function toggle<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

export function InspectionReminderConfigFields({
  value,
  onChange,
  checklistTypeId,
  onChecklistTypeChange,
}: Props) {
  const repeat = value.repeat;
  const everyHours = repeat.mode === 'interval' ? Math.round(repeat.every_minutes / 60) : 24;
  const maxCount = repeat.mode === 'interval' ? repeat.max_count : 3;

  // v1.1.0: status finais só aceitam a referência "entrada no status"
  const hasFinal = hasFinalInspectionStatus(value.statuses);

  /** Alterna status; ao marcar um finalizado, força a referência "entrada no status" */
  const toggleStatus = (status: string) => {
    const statuses = toggle(value.statuses, status);
    const anchor = hasFinalInspectionStatus(statuses) ? 'status_entered' : value.anchor;
    onChange({ ...value, statuses, anchor });
  };

  return (
    <div className="space-y-4 rounded-lg border p-3">
      {/* Status que disparam, agrupados em aberto / finalizada */}
      <div className="space-y-2">
        <Label className="text-xs">Status da vistoria</Label>
        {(['open', 'final'] as const).map((group) => (
          <div key={group} className="space-y-1.5">
            <p className="text-[11px] text-muted-foreground">
              {group === 'open' ? 'Em aberto' : 'Finalizada (aviso de encerramento)'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {INSPECTION_REMINDER_STATUSES.filter((s) => s.group === group).map((s) => (
                <label key={s.value} className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox
                    checked={value.statuses.includes(s.value)}
                    onCheckedChange={() => toggleStatus(s.value)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Referência do deslocamento */}
      <div className="space-y-2">
        <Label className="text-xs">Contar o momento do disparo a partir de</Label>
        <Select
          value={value.anchor}
          onValueChange={(v) => onChange({ ...value, anchor: v as InspectionReminderConfig['anchor'] })}
        >
          <SelectTrigger className="h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INSPECTION_ANCHORS.map((a) => (
              <SelectItem key={a.value} value={a.value} disabled={hasFinal && a.value !== 'status_entered'}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFinal && (
          <p className="text-[11px] text-muted-foreground">
            Com status finalizado marcado, o envio conta a partir da entrada no status (prazos valem só para vistorias em aberto).
          </p>
        )}
        {value.anchor === 'status_entered' && (
          <p className="text-[11px] text-muted-foreground">
            Para "tempo no status", escolha um momento em "Depois" (ex.: 1 hora depois).
          </p>
        )}
      </div>

      {/* Quem recebe */}
      <div className="space-y-2">
        <Label className="text-xs">Quem recebe</Label>
        <div className="flex flex-wrap gap-3">
          {INSPECTION_RECIPIENT_MODES.map((m) => (
            <label key={m.value} className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={value.recipient_modes.includes(m.value)}
                onCheckedChange={() =>
                  onChange({ ...value, recipient_modes: toggle(value.recipient_modes, m.value) })
                }
              />
              {m.label}
            </label>
          ))}
        </div>
      </div>

      {/* Repetição */}
      <div className="space-y-2">
        <Label className="text-xs">Repetição</Label>
        <Select
          value={repeat.mode}
          onValueChange={(v) =>
            onChange({
              ...value,
              repeat: v === 'interval'
                ? { mode: 'interval', every_minutes: everyHours * 60, max_count: maxCount }
                : { mode: 'once' },
            })
          }
        >
          <SelectTrigger className="h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="once">Enviar uma vez</SelectItem>
            <SelectItem value="interval">Repetir</SelectItem>
          </SelectContent>
        </Select>
        {repeat.mode === 'interval' && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">A cada (horas)</Label>
              <Input
                type="number"
                min={1}
                max={720}
                className="h-8 text-xs"
                value={everyHours}
                onChange={(e) => {
                  const h = Math.min(720, Math.max(1, Number(e.target.value) || 1));
                  onChange({ ...value, repeat: { mode: 'interval', every_minutes: h * 60, max_count: maxCount } });
                }}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Máximo de envios</Label>
              <Input
                type="number"
                min={2}
                max={30}
                className="h-8 text-xs"
                value={maxCount}
                onChange={(e) => {
                  const n = Math.min(30, Math.max(2, Number(e.target.value) || 2));
                  onChange({ ...value, repeat: { mode: 'interval', every_minutes: everyHours * 60, max_count: n } });
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Filtro opcional por Tipo de Checklist */}
      <div className="space-y-2">
        <Label className="text-xs">Tipo de Checklist (opcional)</Label>
        <ChecklistTypeFilter value={checklistTypeId} onChange={onChecklistTypeChange} />
      </div>
    </div>
  );
}
